import { IMAGE_LOADER, ImageLoaderConfig } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { ScrollPosition } from "@dtm-frontend/shared/ui";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";

export interface ScheduleComponentState {
    isRightScrollButtonDisabled: boolean;
    isLeftScrollButtonDisabled: boolean;
    activeLanguage: string | undefined;
}

const DEFAULT_OFFSET_VALUE = 100;

@Component({
    selector: "dtm-main-page-lib-schedule",
    templateUrl: "./schedule.component.html",
    styleUrls: ["./schedule.component.scss"],
    providers: [
        LocalComponentStore,
        {
            provide: IMAGE_LOADER,
            useValue: (config: ImageLoaderConfig) => {
                const fileName = config.src;
                const fileExtensionMatch = fileName.match(/\.(\w+)$/);
                const fileExtension = fileExtensionMatch?.[1];

                if (!fileExtension || !config.width || config.width <= 100) {
                    return fileName;
                }

                return fileName.replace(`.${fileExtension}`, `@2x.${fileExtension}`);
            },
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleComponent {
    @ViewChild("scrollArea") private scrollArea!: ElementRef;

    @Input() public set activeLanguage(value: string | undefined) {
        this.localStore.patchState({ activeLanguage: value });
    }

    protected readonly isRightScrollButtonDisabled$ = this.localStore.selectByKey("isRightScrollButtonDisabled");
    protected readonly isLeftScrollButtonDisabled$ = this.localStore.selectByKey("isLeftScrollButtonDisabled");
    protected readonly activeLanguage$ = this.localStore.selectByKey("activeLanguage");

    constructor(private readonly localStore: LocalComponentStore<ScheduleComponentState>) {
        this.localStore.setState({
            isRightScrollButtonDisabled: false,
            isLeftScrollButtonDisabled: false,
            activeLanguage: undefined,
        });
    }

    protected scrollRight(): void {
        this.scrollArea.nativeElement.scrollTo({
            left: this.scrollArea.nativeElement.scrollLeft + DEFAULT_OFFSET_VALUE,
            behavior: "smooth",
        });
    }

    protected scrollLeft(): void {
        this.scrollArea.nativeElement.scrollTo({
            left: this.scrollArea.nativeElement.scrollLeft - DEFAULT_OFFSET_VALUE,
            behavior: "smooth",
        });
    }

    protected changeScrollButtonState(scrollPosition: ScrollPosition): void {
        this.localStore.patchState({
            isLeftScrollButtonDisabled: this.shouldDisableLeftScrollButton(scrollPosition.scrollLeft),
            isRightScrollButtonDisabled: this.shouldDisableRightScrollButton(scrollPosition),
        });
    }

    private shouldDisableLeftScrollButton(scrollLeft: number): boolean {
        return scrollLeft === 0;
    }

    private shouldDisableRightScrollButton({ scrollLeft, offsetWidth, scrollWidth }: ScrollPosition): boolean {
        return Math.ceil(scrollLeft + offsetWidth) >= scrollWidth;
    }
}
